import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import CharacterModel from './CharacterModel';
import { useCharacterController } from './CharacterController';
import ThirdPersonCamera from './ThirdPersonCamera';
import { type BuildingCollider } from './CollisionDetection';

interface PlayerProps {
  spawnPoint: [number, number, number];
  buildingPositions: BuildingCollider[];
  moveTargetRef: React.MutableRefObject<THREE.Vector3 | null>;
  onMoveStart: () => void;
}

/**
 * Player — ties together character controller, model, and follow camera.
 * Camera is locked behind the character; movement is tank-style WASD.
 * Supports click-to-move via moveTargetRef.
 * Passes stateRef directly to CharacterModel so animation reads live isMoving.
 */
export default function Player({ spawnPoint, buildingPositions, moveTargetRef, onMoveStart }: PlayerProps) {
  const groupRef = useRef<THREE.Group>(null);
  const rotationRef = useRef<THREE.Group>(null);
  const forwardRef = useRef<THREE.Vector3>(new THREE.Vector3(0, 0, -1));

  // Colliders already have correct sizes from CityScene
  const { stateRef } = useCharacterController(buildingPositions, spawnPoint, moveTargetRef);

  // Detect movement start (for hiding marker)
  const wasMoving = useRef(false);
  useFrame(() => {
    const isMoving = stateRef.current.isMoving;
    if (isMoving && !wasMoving.current) {
      onMoveStart();
    }
    wasMoving.current = isMoving;
  });

  useFrame(() => {
    if (!groupRef.current) return;
    const state = stateRef.current;

    // Sync position
    groupRef.current.position.copy(state.position);

    // Sync rotation
    if (rotationRef.current) {
      rotationRef.current.rotation.y = state.facingAngle;
    }

    // Update forward direction (for camera)
    forwardRef.current.set(
      Math.sin(state.facingAngle),
      0,
      Math.cos(state.facingAngle),
    ).normalize();
  });

  return (
    <>
      {/* Character mesh */}
      <group ref={groupRef}>
        <group ref={rotationRef}>
          <CharacterModel stateRef={stateRef} />
        </group>
      </group>

      {/* Fixed behind-the-back camera */}
      <ThirdPersonCamera
        target={stateRef.current.position}
        forward={forwardRef.current}
      />
    </>
  );
}
